import React, { FC, useState } from 'react';
import { ReactComponent as BackIcon } from 'assets/icons/back_arrow.svg';
import styles from './scopeThreeHeader.module.scss';
import useRedirect from 'shared/utils/useRedirect';
import { ReactNode } from 'react';
import COButton from '../COButton';
import { ButtonSize } from 'enums/ButtonEnums';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { ReactComponent as UploadFile } from 'assets/icons/Upload File.svg';
import { Col, Row } from 'antd';
import ConfirmationModal from 'views/CreateProductFlow/RawMaterial/ConfirmationModal';
import UploadModal from 'views/ActivityDataEntry/UploadModal';
import { scope3 } from 'constants/constants';
import { useParams } from 'react-router-dom';
import { queryParamsList } from 'views/ActivityDataEntry';
import useFilters from 'shared/hooks/useFilters';
import { ActivityDataEntryScopeTabs } from 'enums/ScopeThreeCategoryStatusEnums';
import { useUserRole } from 'shared/hooks/useUserRole';
import { isViewer } from 'shared/utils/roleUtils';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/notificationTypes';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { resolveScopeThreeHeaderLayoutMode } from 'shared/utils/scopeThreeEditModeQuery';

interface HeaderProps {
  icon: ReactNode;
  title: string;
  year: string;
  isUploadExist?: boolean;
  onSubmit: () => void;
  uploadCallBack?: () => Promise<void>;
  downloadTemplate?: () => void;
  loading?: boolean;
  isSubmitDisabled?: boolean;
  category?: string;
  /**
   * When set, toggles Scope 1–style view vs edit chrome (Edit / Cancel + Save).
   * Omit on legacy pages to keep always-editable header (Submit only).
   */
  isEditView?: boolean;
  onEnterEdit?: () => void;
  onCancelEdit?: () => void | Promise<void>;
}

const ScopeThreeHeader: FC<HeaderProps> = props => {
  const {
    icon,
    title,
    year,
    isUploadExist,
    onSubmit,
    loading,
    uploadCallBack,
    downloadTemplate,
    isSubmitDisabled,
    category,
    isEditView,
    onEnterEdit,
    onCancelEdit,
  } = props;
  const { id } = useParams();
  const { getParams } = useFilters();
  const { activeTab } = getParams(queryParamsList);
  const { redirectToActivityDataEntry } = useRedirect();
  const userRole = useUserRole();
  const isViewerRole = isViewer(userRole);
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] =
    useState<boolean>(false);
  // Will add proper handlers while integrating
  const [uploadModalVisible, setUploadModalVisible] = useState(false);

  const toggleUploadModal = () => setUploadModalVisible(prev => !prev);

  const toggleConfirmationModal = () =>
    setIsConfirmationModalVisible(prev => !prev);

  const handleBack = () =>
    redirectToActivityDataEntry(
      id,
      {
        activeTab,
        scopeTab: ActivityDataEntryScopeTabs.SCOPE_3_UPSTREAM,
      },
      true
    );

  const successCallback = async () => {
    toggleUploadModal();
    await uploadCallBack?.();
  };

  const { legacyMode, inEditMode, inViewMode } =
    resolveScopeThreeHeaderLayoutMode(isEditView);

  /** Non-edit (managed view): leave immediately. Edit + legacy always confirm before leaving. */
  const requestNavigateBack = () => {
    if (inViewMode) {
      setIsConfirmationModalVisible(false);
      handleBack();
      return;
    }
    setIsConfirmationModalVisible(true);
  };

  const subInfo = (
    <Row justify="center" className={styles.subInfo}>
      <Col span={20}>
        Closing the form will result in the loss of any data entered
      </Col>
    </Row>
  );
  return (
    <div className={styles.headerContainer}>
      <div className={styles.detailsContainer}>
        <div className={styles.iconWrapper} onClick={requestNavigateBack}>
          <BackIcon />
        </div>
        <div>{icon}</div>
        <div className={styles.title}>{title}</div>
        <div className={styles.yearBadge}>{year ?? 'Jan 2023 - Dec 2023'}</div>
      </div>

      <div className={styles.actionContainer}>
        {isUploadExist && inEditMode && (
          <Row className={styles.templateButtons}>
            <Col className={styles.button} onClick={downloadTemplate}>
              <DownloadIcon /> <span>Download Template</span>
            </Col>
            <Col onClick={toggleUploadModal} className={styles.button}>
              <UploadFile /> <span>Upload Template</span>
            </Col>
          </Row>
        )}
        <div className={styles.buttonContainer}>
          {/* <COButton
            secondary
            size={ButtonSize.SMALL}
            disabled={!!loading}
            onClick={requestNavigateBack}>
            Go Back
          </COButton> */}
          {inViewMode ? (
            <COButton
              text
              icon={<EditIcon />}
              size={ButtonSize.SMALL}
              onClick={() => {
                if (isViewerRole) {
                  Notification({
                    message:
                      "You don't have admin access to add location and year",
                    description: '',
                    type: NotificationTypes.WARNING,
                  });
                  return;
                }
                onEnterEdit?.();
              }}
              disabled={isViewerRole}>
              Edit
            </COButton>
          ) : (
            <>
              {!legacyMode && (
                <COButton
                  text
                  size={ButtonSize.SMALL}
                  onClick={() => onCancelEdit?.()}
                  disabled={!!loading}>
                  Cancel
                </COButton>
              )}
              <COButton
                primary
                size={ButtonSize.SMALL}
                onClick={() => {
                  if (isViewerRole) {
                    Notification({
                      message:
                        "You don't have admin access to add location and year",
                      description: '',
                      type: NotificationTypes.WARNING,
                    });
                    return;
                  }
                  onSubmit();
                }}
                disabled={isSubmitDisabled || isViewerRole}
                loading={!!loading}>
                Save
              </COButton>
            </>
          )}
        </div>
      </div>
      <ConfirmationModal
        showModal={isConfirmationModalVisible}
        onCancel={toggleConfirmationModal}
        onConfirm={() => {
          setIsConfirmationModalVisible(false);
          handleBack();
        }}
        confirmationText="Are you sure you want to close the form?"
        confirmButtonText="Close"
        cancelButtonText="Go back"
        subInfo={subInfo}
        width={520}
        confirmationClassName={styles.confirmationModal}
        isCancel
      />
      {uploadModalVisible && <UploadModal
        visible={uploadModalVisible}
        onClose={toggleUploadModal}
        activeScope={scope3}
        successCallback={successCallback}
        category={category}
      />}
    </div>
  );
};

export default ScopeThreeHeader;
